/**
 * RBS Editorial PDP — scroll-triggered reveal + gallery slideshow
 *
 * Scroll reveal: observes [data-rbs-fade] elements, adds .rbs-visible on
 * viewport entry. Hero is handled by CSS animation (rbs-hero-reveal).
 *
 * Gallery: lightweight slideshow for [data-rbs-gallery] — handles
 * arrow clicks, dot clicks, drag/swipe (pointer events), keyboard nav,
 * and counter + dot state updates.
 */

(function () {
  'use strict';

  /* ── Scroll Reveal ──────────────────────────────────── */

  function observe() {
    document.querySelectorAll('[data-rbs-fade]').forEach(function (el) {
      if (!el.classList.contains('rbs-visible')) {
        observer.observe(el);
      }
    });
  }

  if (typeof IntersectionObserver === 'undefined') {
    document.querySelectorAll('[data-rbs-fade]').forEach(function (el) {
      el.classList.add('rbs-visible');
    });
  } else {
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('rbs-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -48px 0px' }
    );

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observe);
    } else {
      observe();
    }

    document.addEventListener('theme:section:load', observe);
  }

  /* ── Gallery Slideshow ──────────────────────────────── */

  function initGallery(gallery) {
    var slides  = gallery.querySelectorAll('[data-rbs-slide]');
    var counter = gallery.querySelector('[data-rbs-counter]');
    var prevBtn = gallery.querySelector('[data-rbs-prev]');
    var nextBtn = gallery.querySelector('[data-rbs-next]');
    var dotBtns = gallery.querySelectorAll('[data-rbs-dot]');
    var total   = slides.length;
    var current = 0;

    if (total <= 1) return;

    function goTo(idx) {
      slides[current].classList.remove('is-active');
      slides[current].setAttribute('aria-hidden', 'true');
      if (dotBtns[current]) dotBtns[current].classList.remove('is-active');

      current = (idx + total) % total;

      slides[current].classList.add('is-active');
      slides[current].setAttribute('aria-hidden', 'false');
      if (dotBtns[current]) dotBtns[current].classList.add('is-active');

      if (counter) {
        // thinspace U+2009 between numbers and slash
        counter.textContent = (current + 1) + '\u2009/\u2009' + total;
      }
    }

    /* Arrow clicks */
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(current + 1); });

    /* Dot clicks */
    dotBtns.forEach(function (dot) {
      dot.addEventListener('click', function () {
        goTo(parseInt(dot.dataset.rbsDot, 10));
      });
    });

    /* Keyboard navigation (gallery must be focused — it has tabindex="0") */
    gallery.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); goTo(current - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1); }
    });

    /* Drag / swipe via Pointer Events (works for mouse + touch) */
    var startX   = 0;
    var startY   = 0;
    var dragging = false;
    var THRESHOLD = 48; // px minimum horizontal movement to trigger slide change

    gallery.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      startX   = e.clientX;
      startY   = e.clientY;
      dragging = true;
    });

    gallery.addEventListener('pointermove', function (e) {
      if (!dragging || e.pointerType !== 'mouse') return;
      gallery.style.cursor = 'grabbing';
    });

    gallery.addEventListener('pointerup', function (e) {
      if (!dragging) return;
      dragging = false;
      if (e.pointerType === 'mouse') gallery.style.cursor = '';

      var dx = e.clientX - startX;
      var dy = e.clientY - startY;

      // Only act if horizontal swipe dominates (avoids interfering with scroll)
      if (Math.abs(dx) > THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.5) {
        goTo(dx < 0 ? current + 1 : current - 1);
      }
    });

    gallery.addEventListener('pointercancel', function (e) {
      dragging = false;
      if (e.pointerType === 'mouse') gallery.style.cursor = '';
    });

    /* Prevent browser image drag from interfering */
    gallery.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  function initAllGalleries() {
    document.querySelectorAll('[data-rbs-gallery]').forEach(function (gallery) {
      if (!gallery.dataset.rbsGalleryInit) {
        gallery.dataset.rbsGalleryInit = '1';
        initGallery(gallery);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllGalleries);
  } else {
    initAllGalleries();
  }

  document.addEventListener('theme:section:load', initAllGalleries);

})();
